import { Component, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { NotificationBoardService } from '../../service/notification-board.service';
import { Subscription } from 'rxjs';
import { NotificationBoard } from '../../model/notification-board';
import { AccountRoutes } from '../../../common/model/account-definition';

@Component({
    selector: 'app-notification-board',
    templateUrl: './notification-board.component.html',
    styleUrls: ['./notification-board.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class NotificationBoardComponent implements OnDestroy, OnInit {
  constructor(private _dataService: NotificationBoardService) {}

  notificationGroups: NotificationGroup[] = [];
  notificationGroupsFiltered: NotificationGroup[] = [];

  selectedNotificationGroup: NotificationGroupKind = null;

  private _dataContext: NotificationBoard;
  private _subscription = new Subscription();

  get allNotificationsCount() {
    return this.notificationGroups.flatMap(n => n.notifications).length;
  }

  filterNotifications(selectedNotificationGroup: NotificationGroupKind = null) {
    this.selectedNotificationGroup = selectedNotificationGroup;

    if (!selectedNotificationGroup) {
      this.notificationGroupsFiltered = this.notificationGroups;
      return;
    }

    this.notificationGroupsFiltered = this.notificationGroups.filter(g => g.kind == selectedNotificationGroup);
  }

  initNotifications() {
    const items: NotificationGroup[] = [];
    if (!this._dataContext) {
      this.notificationGroups = [];
      return;
    }

    const invoicesNotificationsGroup = this.getInvoicesNotificationsGroup();
    if (invoicesNotificationsGroup) {
      items.push(invoicesNotificationsGroup);
    }

    const errandsNotificationsGroup = this.getErrandsNotificationsGroup();
    if (errandsNotificationsGroup) {
      items.push(errandsNotificationsGroup);
    }

    const rentNotificationsGroup = this.getRentNotificationsGroup();
    if (rentNotificationsGroup) {
      items.push(rentNotificationsGroup);
    }

    const cooperativeHousingNotificationsGroup = this.getCooperativeHousingNotificationsGroup();
    if (cooperativeHousingNotificationsGroup) {
      items.push(cooperativeHousingNotificationsGroup);
    }

    this.notificationGroups = items;
    this.filterNotifications();
  }

  private getInvoicesNotificationsGroup() {
    const invoiceNotifications = [];
    if (this._dataContext.overdueInvoices) {
      invoiceNotifications.push({
        count: this._dataContext.overdueInvoices,
        header: this._dataContext.overdueInvoices === 1 ? 'NOTIFICATION_BOARD.OVERDUE_INVOICE' : 'NOTIFICATION_BOARD.OVERDUE_INVOICES',
        href: AccountRoutes.invoices,
        params: { c: 'overdue' },
      });
    }
    if (this._dataContext.notAttestedInvoices) {
      invoiceNotifications.push({
        count: this._dataContext.notAttestedInvoices,
        header:
          this._dataContext.notAttestedInvoices === 1 ? 'NOTIFICATION_BOARD.UNATTESTED_INVOICE' : 'NOTIFICATION_BOARD.UNATTESTED_INVOICES',
        href: AccountRoutes.invoices,
        params: { c: 'notAttested' },
      });
    }
    if (this._dataContext.nonDeliverableEmailInvoices) {
      invoiceNotifications.push({
        count: this._dataContext.nonDeliverableEmailInvoices,
        header:
          this._dataContext.nonDeliverableEmailInvoices === 1
            ? 'NOTIFICATION_BOARD.UNDELIVERED_EMAIL_INVOICE'
            : 'NOTIFICATION_BOARD.UNDELIVERED_EMAIL_INVOICES',
        href: AccountRoutes.invoices,
        params: { d: 'notDeliveredLastWeek' },
      });
    }
    if (this._dataContext.incorrectPayments) {
      invoiceNotifications.push({
        count: this._dataContext.incorrectPayments,
        header:
          this._dataContext.incorrectPayments === 1 ? 'NOTIFICATION_BOARD.INCORRECT_PAYMENT' : 'NOTIFICATION_BOARD.INCORRECT_PAYMENTS',
        href: AccountRoutes.incorrectPayments,
      });
    }
    if (this._dataContext.banktransactionImportsUnManaged) {
      invoiceNotifications.push({
        count: this._dataContext.banktransactionImportsUnManaged,
        header:
          this._dataContext.banktransactionImportsUnManaged === 1 ? 'NOTIFICATION_BOARD.BANK_TRANSACTION_IMPORT_UNMANAGED' : 'NOTIFICATION_BOARD.BANK_TRANSACTIONS_IMPORT_UNMANAGED',
        href: `${AccountRoutes.bankTransactionImports}`,
        params: { transactionStatus: 'unhandled' },
      });
    }

    if (invoiceNotifications.length) {
      return {
        header: 'DASHBOARD.INVOICE_ITEM_HEADER',
        kind: NotificationGroupKind.Invoices,
        notifications: invoiceNotifications,
      };
    }
  }

  private getErrandsNotificationsGroup() {
    const errandNotifications = [];
    if (this._dataContext.newFaultNotifications) {
      errandNotifications.push({
        count: this._dataContext.newFaultNotifications,
        header:
          this._dataContext.newFaultNotifications === 1
            ? 'NOTIFICATION_BOARD.NEW_FAULT_NOTIFICATION'
            : 'NOTIFICATION_BOARD.NEW_FAULT_NOTIFICATIONS',
        href: AccountRoutes.faultNotifications,
        params: { status: 'unread' },
      });
    }

    if (errandNotifications.length) {
      return {
        header: 'DASHBOARD.ERRANDS_ITEM_HEADER',
        kind: NotificationGroupKind.Errands,
        notifications: errandNotifications,
      };
    }
  }

  private getRentNotificationsGroup() {
    const rentNotifications = [];
    if (this._dataContext.draftLeaseAgreementsDwelling) {
      rentNotifications.push({
        count: this._dataContext.draftLeaseAgreementsDwelling,
        header:
          this._dataContext.draftLeaseAgreementsDwelling === 1
            ? 'NOTIFICATION_BOARD.DRAFT_DWELLING_LEASEAGREEMENT'
            : 'NOTIFICATION_BOARD.DRAFT_DWELLING_LEASEAGREEMENTS',
        href: `${AccountRoutes.dwellingContracts}`,
        params: { status: 'draft' },
      });
    }
    if (this._dataContext.draftLeaseAgreementsPremises) {
      rentNotifications.push({
        count: this._dataContext.draftLeaseAgreementsPremises,
        header:
          this._dataContext.draftLeaseAgreementsPremises === 1
            ? 'NOTIFICATION_BOARD.DRAFT_PREMISES_LEASEAGREEMENT'
            : 'NOTIFICATION_BOARD.DRAFT_PREMISES_LEASEAGREEMENTS',
        href: `${AccountRoutes.premisesContracts}`,
        params: { status: 'draft' },
      });
    }
    if (this._dataContext.draftLeaseAgreementsParking) {
      rentNotifications.push({
        count: this._dataContext.draftLeaseAgreementsParking,
        header:
          this._dataContext.draftLeaseAgreementsParking === 1
            ? 'NOTIFICATION_BOARD.DRAFT_PARKING_LEASEAGREEMENT'
            : 'NOTIFICATION_BOARD.DRAFT_PARKING_LEASEAGREEMENTS',
        href: `${AccountRoutes.parkingContracts}`,
        params: { status: 'draft' },
      });
    }
    if (this._dataContext.draftLeaseAgreementsOther) {
      rentNotifications.push({
        count: this._dataContext.draftLeaseAgreementsOther,
        header:
          this._dataContext.draftLeaseAgreementsOther === 1
            ? 'NOTIFICATION_BOARD.DRAFT_OTHER_LEASEAGREEMENT'
            : 'NOTIFICATION_BOARD.DRAFT_OTHER_LEASEAGREEMENTS',
        href: `${AccountRoutes.otherContracts}`,
        params: { status: 'draft' },
      });
    }
    if (this._dataContext.fixedTermExpirationLeaseAgreementsDwelling) {
      rentNotifications.push({
        count: this._dataContext.fixedTermExpirationLeaseAgreementsDwelling,
        header: 'NOTIFICATION_BOARD.FIXED_TERM_EXPIRATION_DWELLING_LEASEAGREEMENT',
        href: AccountRoutes.dwellingContracts,
        params: { fixedTermExpirationStatus: 3 },
      });
    }
    if (this._dataContext.fixedTermExpirationLeaseAgreementsPremises) {
      rentNotifications.push({
        count: this._dataContext.fixedTermExpirationLeaseAgreementsPremises,
        header: 'NOTIFICATION_BOARD.FIXED_TERM_EXPIRATION_PREMISES_LEASEAGREEMENT',
        href: AccountRoutes.premisesContracts,
        params: { fixedTermExpirationStatus: 3 },
      });
    }
    if (this._dataContext.fixedTermExpirationLeaseAgreementsParking) {
      rentNotifications.push({
        count: this._dataContext.fixedTermExpirationLeaseAgreementsParking,
        header: 'NOTIFICATION_BOARD.FIXED_TERM_EXPIRATION_PARKING_LEASEAGREEMENT',
        href: AccountRoutes.parkingContracts,
        params: { fixedTermExpirationStatus: 3 },
      });
    }
    if (this._dataContext.fixedTermExpirationLeaseAgreementsOther) {
      rentNotifications.push({
        count: this._dataContext.fixedTermExpirationLeaseAgreementsOther,
        header: 'NOTIFICATION_BOARD.FIXED_TERM_EXPIRATION_OTHER_LEASEAGREEMENT',
        href: AccountRoutes.otherContracts,
        params: { fixedTermExpirationStatus: 3 },
      });
    }
    if (this._dataContext.fixedTermExpirationLeaseAgreementsBlock) {
      rentNotifications.push({
        count: this._dataContext.fixedTermExpirationLeaseAgreementsBlock,
        header: 'NOTIFICATION_BOARD.FIXED_TERM_EXPIRATION_BLOCK_LEASEAGREEMENT',
        href: AccountRoutes.blockContracts,
        params: { fixedTermExpirationStatus: 3 },
      });
    }
    if (this._dataContext.securityOfTenureExpirationLeaseAgreementsDwelling) {
      rentNotifications.push({
        count: this._dataContext.securityOfTenureExpirationLeaseAgreementsDwelling,
        header: 'NOTIFICATION_BOARD.SECURITY_OF_TENURE_EXPIRATION_DWELLING_LEASEAGREEMENT',
        href: AccountRoutes.dwellingContracts,
        params: { securityOfTenureExpirationStatus: 3 },
      });
    }
    if (this._dataContext.securityOfTenureExpirationLeaseAgreementsPremises) {
      rentNotifications.push({
        count: this._dataContext.securityOfTenureExpirationLeaseAgreementsPremises,
        header: 'NOTIFICATION_BOARD.SECURITY_OF_TENURE_EXPIRATION_PREMISES_LEASEAGREEMENT',
        href: AccountRoutes.premisesContracts,
        params: { securityOfTenureExpirationStatus: 3 },
      });
    }
    if (this._dataContext.securityOfTenureExpirationLeaseAgreementsParking) {
      rentNotifications.push({
        count: this._dataContext.securityOfTenureExpirationLeaseAgreementsParking,
        header: 'NOTIFICATION_BOARD.SECURITY_OF_TENURE_EXPIRATION_PARKING_LEASEAGREEMENT',
        href: AccountRoutes.parkingContracts,
        params: { securityOfTenureExpirationStatus: 3 },
      });
    }
    if (this._dataContext.securityOfTenureExpirationLeaseAgreementsOther) {
      rentNotifications.push({
        count: this._dataContext.securityOfTenureExpirationLeaseAgreementsOther,
        header: 'NOTIFICATION_BOARD.SECURITY_OF_TENURE_EXPIRATION_OTHER_LEASEAGREEMENT',
        href: AccountRoutes.otherContracts,
        params: { securityOfTenureExpirationStatus: 3 },
      });
    }
    if (this._dataContext.movingOutTenants) {
      rentNotifications.push({
        count: this._dataContext.movingOutTenants,
        header: this._dataContext.movingOutTenants === 1 ? 'NOTIFICATION_BOARD.MOVING_OUT_TENANT' : 'NOTIFICATION_BOARD.MOVING_OUT_TENANTS',
        href: `${AccountRoutes.dwellingContracts}`,
        params: { status: 'terminated' },
      });
    }
    if (this._dataContext.movingOutPremises) {
      rentNotifications.push({
        count: this._dataContext.movingOutPremises,
        header:
          this._dataContext.movingOutPremises === 1
            ? 'NOTIFICATION_BOARD.MOVING_OUT_PREMISES'
            : 'NOTIFICATION_BOARD.MOVING_OUT_PREMISES_PLURAL',
        href: `${AccountRoutes.premisesContracts}`,
        params: { status: 'terminated' },
      });
    }
    if (this._dataContext.terminationMonitoringLeaseAgreementsDwelling) {
      rentNotifications.push({
        count: this._dataContext.terminationMonitoringLeaseAgreementsDwelling,
        header:
          this._dataContext.terminationMonitoringLeaseAgreementsDwelling === 1
            ? 'NOTIFICATION_BOARD.TERMINATION_MONITORING_DWELLING_LEASEAGREEMENT'
            : 'NOTIFICATION_BOARD.TERMINATION_MONITORING_DWELLING_LEASEAGREEMENTS',
        href: `${AccountRoutes.dwellingContracts}`,
        params: { terminationMonitoringStatus: 'threeMonths' },
      });
    }
    if (this._dataContext.terminationMonitoringLeaseAgreementsPremises) {
      rentNotifications.push({
        count: this._dataContext.terminationMonitoringLeaseAgreementsPremises,
        header:
          this._dataContext.terminationMonitoringLeaseAgreementsPremises === 1
            ? 'NOTIFICATION_BOARD.TERMINATION_MONITORING_PREMISES_LEASEAGREEMENT'
            : 'NOTIFICATION_BOARD.TERMINATION_MONITORING_PREMISES_LEASEAGREEMENTS',
        href: `${AccountRoutes.premisesContracts}`,
        params: { terminationMonitoringStatus: 'threeMonths' },
      });
    }
    if (this._dataContext.terminationMonitoringLeaseAgreementsParking) {
      rentNotifications.push({
        count: this._dataContext.terminationMonitoringLeaseAgreementsParking,
        header:
          this._dataContext.terminationMonitoringLeaseAgreementsParking === 1
            ? 'NOTIFICATION_BOARD.TERMINATION_MONITORING_PARKING_LEASEAGREEMENT'
            : 'NOTIFICATION_BOARD.TERMINATION_MONITORING_PARKING_LEASEAGREEMENTS',
        href: `${AccountRoutes.parkingContracts}`,
        params: { terminationMonitoringStatus: 'threeMonths' },
      });
    }
    if (this._dataContext.terminationMonitoringLeaseAgreementsOther) {
      rentNotifications.push({
        count: this._dataContext.terminationMonitoringLeaseAgreementsOther,
        header:
          this._dataContext.terminationMonitoringLeaseAgreementsOther === 1
            ? 'NOTIFICATION_BOARD.TERMINATION_MONITORING_OTHER_LEASEAGREEMENT'
            : 'NOTIFICATION_BOARD.TERMINATION_MONITORING_OTHER_LEASEAGREEMENT',
        href: `${AccountRoutes.otherContracts}`,
        params: { terminationMonitoringStatus: 'threeMonths' },
      });
    }

    if (rentNotifications.length) {
      return {
        header: 'DASHBOARD.LEASE_AGREEMENT_ITEM_HEADER',
        kind: NotificationGroupKind.Rent,
        notifications: rentNotifications,
      };
    }
  }

  private getCooperativeHousingNotificationsGroup() {
    const cooperativeHousingNotifications = [];
    if (this._dataContext.draftLeaseAgreementsCooperativeHousing) {
      cooperativeHousingNotifications.push({
        count: this._dataContext.draftLeaseAgreementsCooperativeHousing,
        header: 'NOTIFICATION_BOARD.DRAFT_COOPERATIVE_HOUSING_LEASEAGREEMENT',
        href: AccountRoutes.cooperativeHousingContracts,
        params: { status: 'draft' },
      });
    }

    if (cooperativeHousingNotifications.length) {
      return {
        header: 'DASHBOARD.MENU.COOPERATIVE_HOUSING',
        kind: NotificationGroupKind.CooperativeHousing,
        notifications: cooperativeHousingNotifications,
      };
    }
  }

  ngOnDestroy() {
    this._subscription.unsubscribe();
  }

  ngOnInit() {
    this._subscription.add(
      this._dataService.get().subscribe(data => {
        this._dataContext = data;
        this.initNotifications();
      })
    );
  }
}

enum NotificationGroupKind {
  Unknown = 0,
  Invoices = 1,
  Errands = 2,
  Rent = 3,
  CooperativeHousing = 4,
}

class Notification {
  count: number = 0;
  header: string;
  href: string;
  params?: any;
}

class NotificationGroup {
  header: string;
  kind: NotificationGroupKind;
  notifications: Notification[] = [];
}
